import { useEffect, useState, Dispatch, SetStateAction } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import {
  Box,
  Grid,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { addSeconds, addHours, eachDayOfInterval, addDays } from "date-fns";
import Calendar from "react-calendar";
import { TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ExpandMore } from "@mui/icons-material";
import ComputerIcon from "@mui/icons-material/Computer";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";

import { dateConflict, useGetAppointmentsQuery, withinTwelveHours } from "../../../features/appointments/appointmentHooks";
import { IAppointment } from "../../../types/project";
import { IUser } from "../../../types/user";
import { Container } from "../../../commonStyles";
import { ISchedule, IScheduleSetting } from "../../../types/schedule";
import { convertAllDates } from "../../../utils/function";
import { IProject } from "../../../types/project";
import "react-calendar/dist/Calendar.css";
import "./AppointmentCalendar.css";

const AppointmentCalendar = ({
  user,
  setDate,
  setFormError,
  appointment,
  schedules,
  schedule,
  setSchedule,
  project,
  setLocation,
  hideCalendar,
  hideTimePicker,
}: AppointmentProps) => {
  const [day, setDay] = useState<Date>(appointment ? new Date(appointment.date) : new Date());
  const [changedMonth, setChangedMonth] = useState<boolean>(false);
  const [appointments, setAppointments] = useState<IAppointment[]>([]);
  const [dateWarning, setDateWarning] = useState<boolean>(false);
  const [dateError, setDateError] = useState<boolean>(false);
  const [dateFormatError, setDateFormatError] = useState<boolean>(false);
  const [timeSlots, setTimeSlots] = useState<IAvailableDate[]>([]);
  const [timeSlot, setTimeSlot] = useState<Date | null>(null);
  const [time, setTime] = useState<Date | null | undefined>(appointment ? new Date(appointment.date) : null);

  const [getCalendar, { loading }] = useLazyQuery(GETCALENDAR, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, "err");
    },
  });

  const [getAppointments] = useLazyQuery(GETAPPOINTMENTS, {
    onCompleted: (data) => {},
  });

  let todaysAppointments = appointments
    .filter((appointment) => {
      const curDate = new Date(appointment.date);
      return curDate.toDateString() === day.toDateString();
    })
    .sort((a: any, b: any) => new Date(a.date).valueOf() - new Date(b.date).valueOf());

  const daysOfTheWeek = ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];
  const monthsOfTheYear = ["Jan.", "Feb.", "Mar.", "Apr.", "May", "Jun.", "Jul.", "Aug.", "Sept.", "Oct.", "Nov.", "Dec."];

  const formatDateToDay = (date: Date) => {
    return `${daysOfTheWeek[date.getDay()]} ${monthsOfTheYear[date.getMonth()]} ${date.getDate()}`;
  };

  const onCompletedAppointments = (data: any) => {
    setAppointments(data);
  };

  const [, { refetch: refetchAppointments }] = useGetAppointmentsQuery(user?._id, day);

  const mergeDayAndTime = (time: Date | null | undefined, day: Date) => {
    if (!time) return null;
    const dateTime = new Date(time);
    return new Date(day.getFullYear(), day.getMonth(), day.getDate(), dateTime.getHours(), dateTime.getMinutes());
  };

  useEffect(() => {
    const get = async () => {
      const res = await refetchAppointments();
      let filteredEvents: any[] = [];
      if (user) {
        await getCalendar({ variables: { user: user?._id } }).then((res) => {
          if (res.data) {
            filteredEvents = res.data.getCalendar[0].events
              .filter(
                (event: any) =>
                  event.start && ((event.organizer && event.organizer.email === user?.email) || event.visibility === "private")
              )
              .map((event: any) => {
                if (event.start.date && event.end.date) {
                  let dates = [new Date(event.start.date)];
                  if (new Date(addDays(new Date(event.start.date.set), 1)) === new Date(event.end.date)) {
                    dates = eachDayOfInterval({
                      start: new Date(addDays(new Date(event.start.date.set), 1)),
                      end: new Date(event.end.date),
                    });
                  }
                  let daysOff = dates.map((date: Date) => {
                    return {
                      date: addHours(date, 5),
                      location: event.location ? event.location : "Busy",
                      project: {
                        name: event.start.date ? "All Day" : "Busy",
                      },
                      purchaserInfo: {
                        firstName: "Busy",
                        lastName: "",
                      },
                      registrant: null,
                      virtual: false,
                    };
                  });
                  return daysOff;
                } else {
                  return {
                    date: event.start.dateTime ? event.start.dateTime : addHours(new Date(event.start.date), 5),
                    endDate: event.end.dateTime ? event.end.dateTime : addHours(new Date(event.end.date), 5),
                    location: event.location ? event.location : "Busy",
                    project: {
                      name: event.start.date ? "All Day" : "Busy",
                    },
                    purchaserInfo: {
                      firstName: "Busy",
                      lastName: "",
                    },
                    registrant: null,
                    virtual: false,
                  };
                }
              });
          }
        });
      }
      onCompletedAppointments([
        ...res.data.appointmentMany.filter((appointment: IAppointment) => !appointment.cancelled),
        ...filteredEvents.flat(),
      ]);
    };
    get();
  }, [refetchAppointments, user, getCalendar, changedMonth]);

  useEffect(() => {
    handleSchedule(schedule?._id!);
  }, [schedule, schedules]);

  const handleSchedule = async (value: string) => {
    let selectedSchedule = schedules.find((schedule: ISchedule) => schedule._id === value);
    await getAppointments({
      variables: {
        project: project?._id,
        today: new Date(new Date().setHours(0, 0, 0, 0)).toUTCString(),
        type: "deal",
        cancelled: false,
        scheduleId: selectedSchedule?._id,
      },
    }).then((res) => {
      if (selectedSchedule) {
        let timeslots: any[] = selectedSchedule.schedules.map((schedule: IScheduleSetting) => {
          let allSlots = [];
          for (let i = schedule.timeStart; i <= schedule.timeEnd - schedule.length * 60; i += schedule.timeBetweenAppointments * 60) {
            let scheduleDate = new Date(new Date(schedule.date!).setHours(0, 0, 0, 0));
            if (new Date(addSeconds(scheduleDate, i)).valueOf() > new Date().valueOf()) {
              let numBooked = 0;
              res.data.appointmentMany?.forEach((appointment: any) => {
                if (new Date(appointment.date!).getTime() === new Date(addSeconds(scheduleDate, i)).getTime()) {
                  numBooked += 1;
                }
              });

              allSlots.push({
                date: new Date(addSeconds(scheduleDate, i)),
                available: schedule.numberOfAppointments - numBooked < 1 ? 0 : schedule.numberOfAppointments - numBooked,
              });
            }
          }
          return allSlots;
        });

        setTimeSlots(timeslots.flat());
        setSchedule(selectedSchedule);
        if (selectedSchedule.locations?.length === 1 && setLocation) {
          setLocation(selectedSchedule.locations[0]);
        }
      } else {
        setTimeSlots([]);
        setSchedule(null);
      }
    });
  };

  return (
    <Grid container spacing={2}>
      {hideCalendar ? null : (
      <Grid item xs={12} sm={user ? 6 : 12}>
        <Calendar
          className="calendar"
          value={day || new Date()}
          minDate={new Date()}
          onActiveStartDateChange={() => {
            setChangedMonth(true);
          }}
          onChange={async (newDay: Date) => {
            setChangedMonth(false);
            setDay(newDay);
          }}
        />
      </Grid>
      )}
      {user && !hideCalendar ? (
        <Grid item xs={12} sm={6}>
          <Container>
            <Typography>{user?.firstName}'s schedule on</Typography>
            <Typography sx={{ mb: 1 }}>{formatDateToDay(day)}:</Typography>
            {loading ? (
              <>
                <Skeleton height={25} animation="wave" />
                <Skeleton height={25} animation="wave" />
                <Skeleton height={25} animation="wave" />
                <Skeleton height={25} animation="wave" />
              </>
            ) : todaysAppointments.length > 0 ? (
              <Stack spacing={1}>
                {todaysAppointments.map((curAppointment, key) => {
                  const curDate = new Date(curAppointment.date);
                  let endDate = null;
                  if (curAppointment.endDate) {
                    endDate = new Date(curAppointment.endDate);
                  }
                  return (
                    <Accordion key={key} sx={{ border: "1px solid black", borderRadius: "5px" }}>
                      <AccordionSummary sx={{ borderBottom: "1px solid black" }} expandIcon={<ExpandMore />}>
                        <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", fontSize: { xs: 12, sm: 15 } }}>
                          <strong>
                            {curAppointment.project?.name === "Busy"
                              ? `${convertAllDates(curDate, "pp")} ${curAppointment.endDate ? `- ${convertAllDates(endDate, "pp")}` : null}`
                              : curAppointment.project?.name === "All Day"
                              ? "All Day"
                              : convertAllDates(curDate, "pp")}{" "}
                            - {curAppointment?.project?.name}
                          </strong>
                          {curAppointment?.virtual && <ComputerIcon />}
                          {appointment?._id && appointment?._id === curAppointment?._id && <RadioButtonCheckedIcon />}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={1}>
                          <Typography>
                            <strong>Client:</strong> {curAppointment.purchaserInfo.firstName} {curAppointment.purchaserInfo.lastName}
                          </Typography>
                          <Typography>
                            <strong>Location:</strong> {curAppointment.virtual ? "Virtual" : curAppointment.location}
                          </Typography>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            ) : (
              <Typography>
                <strong>None</strong>
              </Typography>
            )}
          </Container>
        </Grid>
      ) : null}
      {schedules.length ? (
        <Grid item xs={12}>
          <Box sx={{ mt: 2 }}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel id="demo-simple-select-label">Events</InputLabel>
              <Select
                label="Events"
                sx={{
                  width: "100%",
                  "& .MuiSelect-select": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={schedule ? schedule._id : ""}
                onChange={(e: any) => {
                  handleSchedule(e.target.value);
                }}
              >
                <MenuItem value="">None</MenuItem>
                {schedules.map((schedule: ISchedule, index: number) => {
                  return (
                    <MenuItem key={index} value={schedule._id} sx={{ whiteSpace: "normal" }}>
                      {schedule.project.name} - {schedule.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        </Grid>
      ) : null}
      {schedule ? (
        <Grid item xs={12}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel id="timeslot-label">Time Slot</InputLabel>
            <Select
              labelId="timeslot-label"
              value={timeSlot}
              label="Time Slot"
              onChange={(e) => {
                setDay(new Date(e.target.value!));
                setTimeSlot(new Date(e.target.value!));
                setTime(new Date(e.target.value!));
                setDate(new Date(e.target.value!));
              }}
            >
              {timeSlots.map((timeSlot: IAvailableDate, index: number) => {
                return (
                  <MenuItem key={index} value={timeSlot.date.toString()}>
                    {" "}
                    {convertAllDates(timeSlot.date, "PPpp")} - {timeSlot.available} Available Slots Left
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      ) : null}
      {hideTimePicker ? null : (
      <Grid item xs={12}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <TimePicker
            label={appointment ? "Appointment Time" : "New Appointment Time"}
            value={time}
            onChange={(newTime) => {
              if (!newTime || !Date.parse(newTime.toString())) {
                setDateFormatError(true);
                return;
              }
              setDateFormatError(false);
              setTime(newTime);
              setDate(mergeDayAndTime(newTime, day));
              if (withinTwelveHours(mergeDayAndTime(newTime, day))) {
                setDateWarning(true);
              } else {
                setDateWarning(false);
              }
              if (dateConflict(mergeDayAndTime(newTime, day), appointments, appointment)) {
                setDateError(true);
              } else {
                setDateError(false);
              }
            }}
          />

          {dateWarning && (
            <Typography sx={{ fontSize: 10, color: "#c69035" }}>The time chosen is within 12 hours of the current moment.</Typography>
          )}
          {dateError && (
            <Typography sx={{ fontSize: 10, color: "#c69035" }}>This time conflicts with another of this user's appointment.</Typography>
          )}
          {dateFormatError && <Typography sx={{ fontSize: 10, color: "red" }}>This is an incorrectly formatted date.</Typography>}
        </LocalizationProvider>
      </Grid>
      )}
    </Grid>
  );
};

type AppointmentProps = {
  user: IUser | undefined;
  setDate: (date: Date | null | undefined) => void;
  setFormError: (error: boolean) => void;
  appointment?: IAppointment;
  schedules: ISchedule[];
  schedule: ISchedule | null;
  setSchedule: any;
  project: IProject | null;
  setLocation?: Dispatch<SetStateAction<string>>;
  // Only show the Events, Time Slot and time fields (no month calendar or schedule), e.g. in the call modal
  hideCalendar?: boolean;
  // Hide the time picker so the time can only come from an event's time slot
  hideTimePicker?: boolean;
};

interface IAvailableDate {
  date: Date;
  available: number;
}

const GETCALENDAR = gql`
  query getCalendar($user: MongoID) {
    getCalendar(user: $user) {
      id
      name
      events {
        id
        status
        location
        htmlLink
        summary
        created
        updated
        creator {
          email
        }
        organizer {
          email
          displayName
          self
        }
        start {
          date
          dateTime
          timeZone
        }
        end {
          date
          dateTime
          timeZone
        }
        attendees {
          email
          organizer
          self
          responseStatus
        }
        hangoutLink
        visibility
      }
    }
  }
`;

const GETAPPOINTMENTS = gql`
  query getAppointments($project: MongoID!, $today: String!, $type: String!, $cancelled: Boolean!, $scheduleId: String!) {
    appointmentMany(filter: { project: $project, dateGreaterThanEqual: $today, cancelled: $cancelled }, limit: 10000) {
      date
    }
    getSchedules(project: $project, type: $type, today: $today, cancelled: $cancelled, scheduleId: $scheduleId) {
      _id
      active
      allowGuests
      maxGuests
      public
      schedules {
        date
        length
        timeBetweenAppointments
        timeStart
        timeEnd
        numberOfAppointments
      }
      questions {
        _id
        name
        question
        type
        subQuestion
        display
        choices {
          choice
          followUp {
            question
            _id
          }
        }
      }
      locations
      type
      access
      mainText
      endText
      emailEndText
      emailMainText
      name
      hideLocation
    }
  }
`;

export default AppointmentCalendar;
